import { getLocalAuth } from 'v2/utils'
import { ElevenLabsModels } from '../config/agent-settings.validation.config'
import type { AgentSettingsFormValues } from '../config/base.config'
import type { RProviders } from '../sidebar/types'
import {
  filterVoicesByLanguage,
  findTtsInfoByVoiceId,
  getVoiceAiConfig,
  processLanguageSelection,
  resolveTtsModelForVoice,
} from './lang-utils'

export function validateVoiceAiConfig(
  voiceAiConfig: AgentSettingsFormValues['voice_ai_config'],
  providers?: RProviders,
): AgentSettingsFormValues['voice_ai_config'] {
  const { region } = getLocalAuth()
  if (!voiceAiConfig?.language) {
    return voiceAiConfig
  }

  const rawLanguagesArray = voiceAiConfig.language.split(',').filter(Boolean)
  const selectedLanguagesArray = processLanguageSelection(rawLanguagesArray, region)
  const sttTtsConfig = getVoiceAiConfig(selectedLanguagesArray, region, providers)

  const { text_to_speech: payloadTTS } = voiceAiConfig

  const ttsProvider = providers?.tts_providers?.find(
    (provider) => provider.id === sttTtsConfig.ttsProvider,
  )
  const allProviderVoices = ttsProvider?.voices || []
  const languageFilteredVoices = filterVoicesByLanguage(allProviderVoices, selectedLanguagesArray)
  // Custom voices are voices that are not in the language-filtered provider voices
  const customVoices = allProviderVoices?.filter((voice) => !!voice.meta?.type)

  const currentVoice = payloadTTS?.voice
    ? [...languageFilteredVoices, ...customVoices].find((voice) => voice.id === payloadTTS.voice)
    : undefined

  // Check if voice exists in provider's built-in voices at all
  // Custom/cloned voices won't be in this list - they come from a separate API
  const voiceExistsInProvider = payloadTTS?.voice
    ? allProviderVoices.some((voice) => voice.id === payloadTTS.voice)
    : false

  // Voice is compatible if:
  // 1. No voice is selected, OR
  // 2. Voice is found in the language-filtered provider voices, OR
  // 3. Voice is NOT in provider voices at all (custom/cloned voice - preserve it)
  const isVoiceCompatible = !payloadTTS?.voice || !!currentVoice || !voiceExistsInProvider

  const finalVoice = isVoiceCompatible
    ? payloadTTS?.voice || sttTtsConfig.defaultVoice
    : sttTtsConfig.defaultVoice

  // Resolve provider/model from the final voice selection
  const voiceTtsInfo = findTtsInfoByVoiceId(providers, finalVoice)

  // The user's selected voice is actually the one being applied (not auto-fallbacked
  // to a default). Only then should the flow-level speed override be preserved.
  const isVoicePreserved = !!payloadTTS?.voice && finalVoice === payloadTTS.voice

  return {
    ...voiceAiConfig,
    language: selectedLanguagesArray.join(','), // Update language with processed values
    text_to_speech: {
      provider: voiceTtsInfo?.providerId || sttTtsConfig.ttsProvider,
      // The model must follow the final voice's provider, never a stale payload model:
      // a preserved Sarvam voice with an old ElevenLabs model must resolve to bulbul:v3.
      // With no voice selected there is nothing to derive from, so keep the prior fallback.
      model: finalVoice
        ? resolveTtsModelForVoice(providers, allProviderVoices, finalVoice, payloadTTS?.model)
        : payloadTTS?.model ||
          voiceTtsInfo?.modelId ||
          sttTtsConfig.ttsModel ||
          ElevenLabsModels.TurboV2_5,
      voice: finalVoice,
      voice_label: isVoiceCompatible
        ? currentVoice?.label || payloadTTS?.voice_label || sttTtsConfig.defaultVoiceLabel
        : sttTtsConfig.defaultVoiceLabel,
      // Carry the flow-level speed override only when the selected voice is preserved;
      // when the voice is reset to default, drop it so it re-defaults to the DB speed.
      voice_speed: isVoicePreserved ? payloadTTS?.voice_speed : undefined,
      pronunciation: payloadTTS?.pronunciation || [],
    },
  }
}
